import i from 'i18next'
import { useEffect, useContext, useState, useRef } from 'react'
import { motion, Transition } from 'framer-motion'
import { mobileMenuMaxWidth } from 'constants/styles'
import { Router } from 'next/router'
import NextLink from 'next/link'
import { currency } from 'lib/currency'
import { State } from 'components/Layout'
import { res } from 'components/Image'
import Close from 'components/svg/Close'
import { RadioFilled, RadioEmpty } from 'components/svg/Radio'
import { headers as ease } from 'constants/easings'
import Back from 'components/svg/Back'
import { isPerfumeOption } from 'components/product/Thumbnail'
import { Footer } from './cart/Footer'
import { Item } from './cart/Item'
import PickGift from './cart/PickGift'
import { LineItem, gifts, isEligibleForGift } from 'lib/hooks/use-cart'
import { gtmPush } from 'components/analytics/GTM'
import Trash from 'components/svg/Trash'

export const BENELUX = 1
export const BENELUX_FREE = 40
export const BENELUX_SHIPPING = 5.9
export const EUROPE = 2
export const EUROPE_FREE = 50
export const EUROPE_SHIPPING = 9.99

const WRAP_ENABLED = false
const WRAP_VARIANT_IDS = [
	'gid://shopify/ProductVariant/42012927787194',
	'gid://shopify/ProductVariant/42012927852730',
]
const WRAP: LineItem['variant'] = {
	id: WRAP_VARIANT_IDS[ 0 ],
	title: i.t( 'cart.wrap' ),
	priceV2: {
		amount: '0.00',
	},
	compareAtPrice: {
		amount: '0.00',
	},
	product: {
		id: '',
		title: '',
		productType: 'Free',
		handle: 'gift-wrap',
	},
	image: {
		originalSrc: 'https://cdn.shopify.com/s/files/1/0277/6992/9843/products/2022-11-19raycaregift-5_40x40.jpg?v=1668955092',
	},
	selectedOptions: [],
}

const suggestWrap = ( lineItems: LineItem[] ) => (
	WRAP_ENABLED &&
	lineItems.filter( ( i ) => !!i.variant )
		.filter( isEligibleForGift )
		.filter( ( i ) => i.variant.product.productType !== 'Free' )
		.filter( ( i ) => i.quantity !== 0 ) // optimistic update
		.length > 0
)

const noteTransition: Transition = { duration: 1, ease }

type Props = {
	active: boolean
	onClose: () => void
}

export default function Cart({ active, onClose }: Props ) {
	const { cart, update, gift, addToCart: add } = useContext( State )
	const [ zone, setZone ] = useState<1 | 2>( /*0*/ BENELUX )
	const [ note, setNote ] = useState( false )
	const [ text, setText ] = useState( cart?.note || '' )
	const [ pick, setPick ] = useState<'first' | 'second' | false>( false )

	// Hide menu when navigating to another (dynamic) page
	useEffect( () => {
		if ( active ) {
			Router.events.on( 'routeChangeComplete', onClose )
			return () => Router.events.off( 'routeChangeComplete', onClose )
		}
	}, [ active ] )

	const hideOnBackdropClick = e => {
		if ( e.target.className === 'backdrop' ) onClose()
	}

	// Go back to cart if it was closed on the notes page
	useEffect( () => { !active && setNote( false ) }, [ active ] )

	// Scroll items div to top when switching to/from note
	const items = useRef<HTMLDivElement>()
	useEffect( () => {
		setTimeout( () => items.current?.scrollTo( 0, 0 ), 500 )
	}, [ note ] )

	// Scroll down when a free gift is added
	useEffect( () => {
		if ( cart?.freeGift?.gift || !!cart?.freeGift2?.gift ) {
			items.current?.scrollTo({ top: 2000, behavior: 'smooth' })
		}
	}, [ !!cart?.freeGift?.gift, !!cart?.freeGift2?.gift ] )

	const initial = 'hidden'
	const animate = ( active ? ( note ? 'note' : 'visible' ) : 'hidden' )
	const variants = {
		hidden: {
			x: '100%',
		},
		visible: {
			x: 0,
		},
	}

	const [ isClient, setIsClient ] = useState( false )
	useEffect( () => { setIsClient( true ) }, [] )
	if ( !isClient ) return null

	const wrapping = cart?.lineItems.find( item =>
		WRAP_VARIANT_IDS.indexOf( item.variant.id ) > -1
	)

	return (
		<div className="menu" onClick={hideOnBackdropClick}>
			<motion.div
				{...{ variants, initial, animate }}
				transition={{ duration: 1, ease, delayChildren: 0.5 }}
				className="backdrop"
			>
				<aside className="cart">
					<h2>
						<motion.span
							variants={{
								visible: { x: 0, opacity: 1 },
								note: { x: -300, opacity: 0 },
							}}
							transition={noteTransition}
						>
							{i.t( 'cart.title' )}
							<span className="count">{cart?.count || 0}</span>
						</motion.span>
						<motion.span
							initial={{
								position: 'absolute',
								opacity: 0,
								pointerEvents: 'none',
							}}
							variants={{
								hidden: { opacity: 0, pointerEvents: 'none' },
								visible: { x: 300, opacity: 0, pointerEvents: 'none' },
								note: { x: 0, opacity: 1, pointerEvents: 'auto' },
							}}
							transition={noteTransition}
						>
							<a aria-label="Back" onClick={() => setNote( false )}>
								<Back />
							</a>
							{i.t( 'cart.note.title' )}
						</motion.span>
					</h2>
					<div className="items" ref={items}>
						<div>
							<motion.div
								variants={{
									visible: { x: 0, opacity: 1 },
									note: { x: -500, opacity: 0 },
								}}
								transition={noteTransition}
							>
								{cart?.lineItems
									?.filter( ( i ) => !!i.variant )
									.filter( ( i ) => i.variant.product.productType !== 'Free' )
									.filter( ( i ) => i.quantity !== 0 ) // optimistic update
									.map( ( item ) => (
										<Item key={item.id} item={item} update={update} />
									) )}

								{( wrapping || suggestWrap( cart?.lineItems ?? [] ) ) && (
									<div className="item">
										<picture style={{ padding: 0, height: 100 }}>
											<img
												src="https://cdn.shopify.com/s/files/1/0277/6992/9843/products/2022-11-19raycaregift-5_350x350.jpg?v=1668955092"
											/>
										</picture>
										<div style={{ justifyContent: 'flex-start' }}>
											<h3>{i.t( 'cart.wrap' )}</h3>
											{wrapping ? (
												<button
													className="button-delete"
													onClick={() => update( wrapping.id, 0 )}
													style={{ marginTop: 10, marginLeft: -11 } }
												>
													<Trash />
												</button>
											) : ( <button
												className="button-underline"
												onClick={() => add( WRAP )}
											>
												{i.t( 'cart.wrap.add' )}
											</button> )}
										</div>
										<div
											style={{
												justifyContent: 'flex-start',
												alignItems: 'flex-end',
												textAlign: 'right',
											}}
										>
											<h3>{wrapping ? '√' : currency.format( 0 )}</h3>
										</div>
									</div>
								)}
								{cart?.freeGift?.gift && (
									<div className="item">
										<label>{i.t( 'cart.gift.label' )}</label>
										<picture>
											<img
												className="product"
												src={res(
													cart.freeGift.gift.variant.image?.originalSrc,
													'152x'
												)}
											/>
										</picture>
										<div style={{ justifyContent: 'flex-start' }}>
											<h3>
												{cart.freeGift.gift.variant.selectedOptions[ 0 ].value}
											</h3>
											{gifts.first.length > 1 && (
												<button
													className="button-underline"
													onClick={() => {
														gtmPush({
															event: 'updateCartGift',
															ecommerce: {
																detail: {
																	current:
																		cart.freeGift.gift.variant
																			.selectedOptions[ 0 ].value,
																},
															},
														})
														setPick( 'first' )
													}}
												>
													{i.t( 'cart.gift.change' )}
												</button>
											)}
										</div>
										<div
											style={{
												justifyContent: 'flex-start',
												alignItems: 'flex-end',
												textAlign: 'right',
											}}
										>
											{cart.freeGift.gift.variant.compareAtPrice && (
												<h3 className="discount">
													{currency.format(
														cart.freeGift.gift.variant.compareAtPrice.amount
													)}
												</h3>
											)}
											<h3>{currency.format( 0 )}</h3>
										</div>
									</div>
								)}
								{cart?.freeGift2?.gift && (
									<div className="item" style={{ borderTop: 0, paddingTop: 0 }}>
										<picture>
											<img
												className="product"
												src={res(
													cart.freeGift2.gift.variant.image?.originalSrc,
													'152x'
												)}
											/>
										</picture>
										<div style={{ justifyContent: 'flex-start' }}>
											<h3>
												{cart.freeGift2.gift.variant.selectedOptions[ 0 ].value}
											</h3>
											{gifts.second.length > 1 && (
												<button
													className="button-underline"
													onClick={() => {
														gtmPush({
															event: 'updateCartGift',
															ecommerce: {
																detail: {
																	current:
																		cart.freeGift2.gift.variant
																			.selectedOptions[ 0 ].value,
																},
															},
														})
														setPick( 'second' )
													}}
												>
													{i.t( 'cart.gift.change' )}
												</button>
											)}
										</div>
										<div
											style={{
												justifyContent: 'flex-start',
												alignItems: 'flex-end',
												textAlign: 'right',
											}}
										>
											{cart.freeGift2.gift.variant.compareAtPrice && (
												<h3 className="discount">
													{currency.format(
														cart.freeGift2.gift.variant.compareAtPrice.amount
													)}
												</h3>
											)}
											<h3>{currency.format( 0 )}</h3>
										</div>
									</div>
								)}
							</motion.div>
							<motion.div
								initial={{ position: 'absolute', left: 0, top: 0, opacity: 0 }}
								variants={{
									visible: { x: 500, opacity: 0 },
									note: { x: 0, opacity: 1 },
								}}
								transition={noteTransition}
							>
								<p>{i.t( 'cart.note.explanation' )}</p>

								<label>
									<div>{i.t( 'cart.note.label' )}</div>
									<textarea
										placeholder={i.t( 'cart.note.placeholder' )}
										rows={1}
										value={text}
										onChange={( e ) => setText( e.target.value )}
									/>
								</label>
							</motion.div>
						</div>
					</div>
					{!zone && (
						<div className="shipping select">
							<div role="radiogroup">
								<label>{i.t( 'cart.shipping.select.title' )}</label>
								<div
									role="radio"
									aria-checked={zone === BENELUX}
									tabIndex={0}
									onClick={() => setZone( BENELUX )}
								>
									{zone === BENELUX ? <RadioFilled /> : <RadioEmpty />}
									{i.t( 'cart.shipping.select.benelux', {
										threshold: BENELUX_FREE,
									})}
								</div>
								<div
									role="radio"
									aria-checked={zone === EUROPE}
									tabIndex={0}
									onClick={() => setZone( EUROPE )}
								>
									{zone === EUROPE ? <RadioFilled /> : <RadioEmpty />}
									{i.t( 'cart.shipping.select.europe', {
										threshold: EUROPE_FREE,
									})}
								</div>
							</div>
						</div>
					)}
					<Footer cart={cart} zone={zone} />
					{!note && (
						<a aria-label="Close Menu" className="close" onClick={onClose}>
							<Close size={12} />
						</a>
					)}
				</aside>
				{pick && (
					<aside className="cart gift">
						<a
							aria-label="Close Menu"
							className="close"
							onClick={() => setPick( false )}
						>
							<Close size={12} />
						</a>
						<h2>{i.t( 'cart.gift.change' )}</h2>
						<PickGift
							cart={cart}
							which={pick}
							onChoose={( variantId ) => {
								gift( pick, variantId )
								setPick( false )
							}}
						/>
					</aside>
				)}
			</motion.div>
			<style jsx>{`
				.menu {
					pointer-events: ${active ? 'auto' : 'none'};
				}
				.menu > :global(div) {
					position: fixed;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					z-index: 4;
				}
				.cart {
					position: absolute;
					right: 0;
					height: 100%;
					width: 90%;
					max-width: 495px;
					display: flex;
					flex-direction: column;
					align-items: center;
					background-color: white;
					box-shadow: ${( active ? '6px 2px 29px 0 rgba( 0, 0, 0, 0.15 )' : 'none' )};
					transition: ${( active ? 'none' : 'box-shadow .5s 1s' )};
					overflow: hidden;
				}
				.close {
					position: absolute;
					top: 26px;
					right: 15px;
					padding: 5px 16px;
					cursor: pointer;
				}
				.cart :global( h2 ) {
					width: calc(100% - 60px);
					border-bottom: 1px solid #D8DDE3;
					margin: 0;
					display: flex;
					align-items: stretch;
					font-size: 18px;
					line-height: 80px;
					text-transform: lowercase;
				}
				@media (max-height: 750px) {
					.cart :global( h2 ) {
						line-height: 60px;
						border-bottom: none;
					}
					.close {
						top: 15px;
					}
				}
				.cart :global( h2 .count ) {
					margin-left: 15px;
					width: 26px;
					height: 26px;
					vertical-align: middle;
				}
				.cart :global( h2 a[aria-label="Back"] ) {
					position: absolute;
					left: -42px;
				}
				.shipping {
					position: relative;
					align-self: stretch;
					padding: 26px 60px;
					background-color: #F7F7F7;
					text-align: center;
					font-size: 15px;
					color: #8a8f95;
				}
				@media ( max-width: 500px ) {
					.shipping {
						padding: 16px 30px;
					}
				}
				.shipping :global( span ) {
					width: 100%;
					text-align: center;
				}
				.shipping :global( .progress ) {
					position: absolute;
					top: 0;
					left: 0;
					height: 2px;
					background-color: black;
				}
				.shipping.select {
					text-align: left;
					text-transform: lowercase;
				}
				.shipping.select label {
					display: block;
					margin-top: 14px;
					margin-bottom: 32px;
				}
				.shipping.select [role='radio'] {
					margin-bottom: 4px;
					display: flex;
					align-items: center;
					font-size: 18px;
					color: black;
				}
				.shipping.select [role="radio"] :global( svg ) {
					margin-right: 8px;
				}
				.shipping.select::after {
					content: "";
					display: block;
					height: 0;
					width: 100%;
					transform: translateY( 36px );
					border-bottom: 1px solid #D8DDE3;
				}
				.items {
					align-self: stretch;
					padding: 0 30px;
					flex: 1;
					overflow: auto;
				}
				@media (max-height: 750px) {
					.items {
						padding-right: 14px;
					}
				}
				.items::-webkit-scrollbar {
					width: 16px;
					left: -10px;
				}
				.items::-webkit-scrollbar-thumb {
					background: #8a8f95;
					border-radius: 16px;
					border: 4px solid white;
				}
				.items::-webkit-scrollbar-thumb:hover {
					background: #666;
				}
				.items > div {
					position: relative;
					width: 100%;
					overflow: hidden;
				}

				label {
					margin: 60px 0;
					display: block;
				}
				textarea {
					margin-top: 30px;
					width: 100%;
					text-transform: none;
				}
			`}</style>
			<style jsx global>{`
				.cart .item {
					width: 100%;
					padding-top: 26px;
					margin-bottom: 6px;
					display: grid;
					grid-template-columns: 86px 1fr 36px;
					grid-column-gap: 36px;
				}
				@media (max-height: 750px) {
					.cart .item:first-child {
						padding-top: 4px;
					}
				}
				@media (min-height: 750px) {
					.cart .item {
						margin-bottom: 28px;
					}
					.cart .item + .item {
						border-top: 1px solid #D8DDE3;
					}
				}
				.cart .item label {
					margin: 0 0 1em;
					grid-column: 1/4;
					font-size: 18px;
					text-transform: lowercase;
				}
				.cart .item h3 {
					margin-top: 8px;
					margin-bottom: .5em;
					font-size: 16px;
					font-weight: normal;
					text-transform: lowercase;
					text-overflow: ellipsis;
					overflow: hidden;
					width: 100%;
				}
				@media ( max-width: 500px ) {
					.cart .item h3 {
						font-size: 15px;
					}
				}
				.cart .item picture {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					padding: 10px 5px;
					background-color: #f7f7f7;
				}
				.cart .item picture img.product {
					height: 70px;
					object-fit: contain;
					mix-blend-mode: multiply;
					image-rendering: -webkit-optimize-contrast;
				}
				.cart .item .price {
					text-align: right;
				}
				.cart .item .info {
					margin-top: 1em;
					grid-column: 1/4;
					font-size: 14px;
					line-height: 1.4;
					color: #8a8f95;
				}
				.cart .item > div {
					display: flex;
					flex-direction: column;
					align-items: flex-start;
					justify-content: space-between;
					overflow: hidden;
				}
				.cart .item > button {
					background-color: transparent;
					border: none;
					height: 25px;
					width: 25px;
					padding: 0;
					margin-right: -4px;
					line-height: 1;
					cursor: pointer;
					color: inherit;
				}
				.cart .item .buttons {
					margin-bottom: 8px;
					display: inline-flex;
					align-items: stretch;
					border: 1px solid #D8D8D8;
					cursor: default;
				}
				.cart .item .button-delete {
					background: none;
					border: none;
					padding: 0;
					cursor: pointer;
					color: #8a8f95;
					font-size: 12px;
					margin-bottom: 4px;
					padding-inline: 10px;
					margin-left: -6px;
				}
				.cart .item .button-delete:hover {
					color: black;
				}
				@media ( max-width: 500px ) {
					.cart .item .buttons {
						font-size: 14px;
					}
				}
				.cart .item .buttons button {
					background-color: transparent;
					border: none;
					cursor: pointer;
					color: inherit;
					padding: 0 .6em;
				}
				.cart .item .buttons .quantity {
					min-width: 2em;
					text-align: center;
					line-height: 1.8;
					border: 1px solid #D8D8D8;
					border-width: 0 1px;
				}
				.cart .item .discount {
					color: #B3B3B3;
					text-decoration: line-through;
				}
				.cart .item .discount + h3 {
					margin-top: 0;
				}

				@media (max-width: ${mobileMenuMaxWidth}px) {
					.cart .item {
						grid-template-columns: 86px 1fr 36px;
						grid-column-gap: calc( 10px + 2vw );
					}
				}
			`}</style>
		</div>
	)
}

const getOption = ( options: Array<{ name: string, value: string }>, name ) => (
	options?.find( o => o.name === name )?.value
)

export const Title = ({ item }) => {
	const scent = getOption( item.variant.selectedOptions, 'Perfume' )
	const options = item.variant.selectedOptions
		.filter( o => !isPerfumeOption( o ) )
		.map( o => o.value )
		.filter( value => value !== 'Default Title' )

	return (
		<h3>
			{!!scent && `${scent} `}
			{item.title}
			{!!options.length && ' ' + options.join( ' ' )}
			{' '}
			{item.variant.product.metafields?.[ 'volume-nl' ]}
		</h3>
	)
}

export const getProductLink = ({ product }) => (
	'/products/' +
	( product.variant.product.productType?.toLowerCase() || 'all' ) +
	`/${product.variant.product.handle}`
)

type LinkProps = {
	product: any
	children: any
	className?: string
}

export const Link = ({ product, children, className }: LinkProps ) => (
	<NextLink
		href="/products/[collection]/[...product]"
		as={getProductLink({ product })}
		scroll={false}
		legacyBehavior>
		<a
			className={className}>
			{children}
			<style jsx>{`
				a {
					text-decoration: none;
				}
			`}</style>
		</a>
	</NextLink>
)
